'use client';
// VX L4-10 — KdRouteError: 라우트 error.tsx 공통 본문(KdState 'error' 판 + 기존 동작 그대로).
//   · Sentry 보고(report, 기본 true — 원래 보고하지 않던 경계는 false 로 넘겨 동작을 바꾸지 않는다)
//   · 「다시 시도」 = reset()   · 둘째 버튼 = 그 화면의 상위 목록(back)
//   · autoRetry = 일시 오류(fetch·network·timeout·digest) 3초 뒤 1회 자동 재시도((main)/error.tsx 의 기존 동작)
// ⛔ error.message 를 화면에 그리지 않는다 — 운영에서는 영문 내부 문구가 나온다. 식별은 digest(오류 코드)로.
import React, { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs'; // check-tokens: allow T3 — error.tsx 36곳이 종전부터 쓰던 보고 경로를 한 곳으로 옮긴 것(신규 의존 아님)
import KdState from './KdState';

export interface KdRouteErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
  /** 무엇을 못 불러왔는가(예: 「주식 정보를 불러오지 못했습니다」). */
  title?: string;
  /** 다음 행동 안내. 생략하면 back 에 맞춘 기본 문장. */
  hint?: string;
  /** 둘째 버튼 — 상위 목록. null 이면 「다시 시도」 하나만. */
  back?: { href: string; label: string; hard?: boolean } | null;
  headingAs?: 'h1' | 'h2';
  report?: boolean;
  log?: boolean;
  autoRetry?: boolean;
  /** 루트 error.tsx 처럼 셸 없이 서는 자리 — 세로 여백을 준다. */
  page?: boolean;
}

export default function KdRouteError({ error, reset, title = '페이지를 불러오지 못했습니다', hint, back = { href: '/', label: '홈으로 가기' }, headingAs = 'h2', report = true, log = false, autoRetry = false, page = false }: KdRouteErrorProps) {
  useEffect(() => { if (report) Sentry.captureException(error); }, [error, report]);
  useEffect(() => { if (log) console.error(error); }, [error, log]);
  useEffect(() => {
    if (!autoRetry) return;
    const isTransient = error.message?.includes('fetch') || error.message?.includes('network') || error.message?.includes('timeout') || error.digest;
    if (isTransient) {
      const timer = setTimeout(() => reset(), 3000);
      return () => clearTimeout(timer);
    }
  }, [autoRetry, error, reset]);

  const line = hint ?? (back
    ? `「다시 시도」를 누르면 같은 화면을 다시 불러옵니다. 계속 안 되면 「${back.label}」로 이동해 다시 들어와 주세요.`
    : '「다시 시도」를 누르면 같은 화면을 다시 불러옵니다. 계속 안 되면 네트워크 연결을 확인해 주세요.');

  return (
    <div className="kd-st-wrap" style={page ? { paddingTop: 'var(--sp-2xl)', paddingBottom: 'var(--sp-2xl)' } : undefined}>
      <KdState
        kind="error"
        kicker="일시 오류"
        title={title}
        hint={line}
        headingAs={headingAs}
        code={error?.digest ?? null}
        actions={[
          { label: '다시 시도', onClick: () => reset(), primary: true },
          ...(back ? [{ label: back.label, href: back.href, hard: back.hard }] : []),
        ]}
      />
    </div>
  );
}
